import { faCircleExclamationCheck } from '@fortawesome/pro-regular-svg-icons/faCircleExclamationCheck';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Typography } from "@mui/material";
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcText } from "components";
import { MprcProgress } from "components/MprcProgress";
import { EnrollStatus } from 'generated/CoreApi_v1';
import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { ScoreDetailsDialog } from './ScoreDetailDialog';

/////////////////////////////////////////////////////////////////////////////////////

interface IScoreProps extends React.PropsWithChildren {
    variant: "single" | "multiple";
    enrollmentId: string;
    status: EnrollStatus;
    score: number | null;
    minScore: number;
    isClosed: boolean;
    fullWidth?: boolean;
    totalItems?: number;
    totalCompletedItems?: number;
    enableShowDetails?: boolean;
}

/////////////////////////////////////////////////////////////////////////////////////

export const ScoreResult: React.FC<IScoreProps> = (props) => {
    const { enrollmentId, variant, score, minScore, isClosed, children, fullWidth, totalItems, totalCompletedItems, enableShowDetails } = props;
    const { t } = useTranslation("lms");

    const [showDetails, setShowDetails] = useState<boolean>(false);

    const color =
        (score ?? 0) >= minScore
            ? "success"
            : isClosed
                ? "error"
                : "warning";

    const RenderSingle = (
        <>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <FontAwesomeIcon icon={faCircleExclamationCheck} fontSize={35} fixedWidth />
                <div>
                    <Typography variant={"body2"} component="p" textTransform="uppercase" color={"muted"}>
                        {
                            score
                                ? (
                                    <>
                                        <span>{t("Student.EnrollmentView.ScoreText")}:&nbsp;</span>
                                        <MprcText color={color}><strong>{score}%</strong></MprcText>
                                        <MprcProgress value={score || 0} color={color} />
                                    </>
                                )
                                : t("Student.EnrollmentView.WithoutScoreText")
                        }
                    </Typography>

                    {
                        (totalItems ?? 0) > 0 && minScore > 0 &&
                        <Typography variant="caption" component="p">
                            {t("Student.EnrollmentView.MinFinalScoreText", { x: minScore })}
                        </Typography>
                    }

                    {children}
                </div>
            </Stack>
        </>
    );

    const RenderMutilple = (
        <>
            {enableShowDetails && showDetails &&
                <ScoreDetailsDialog
                    enrollmentId={enrollmentId}
                    onClose={() => setShowDetails(false)}
                />
            }
            <Box className={enableShowDetails ? AppGlobalClassKeys.clickable : undefined} onClick={() => enableShowDetails && setShowDetails(true)}>
                <Typography variant="body2" component="span" textTransform="uppercase">
                    {isClosed
                        ? t("Student.EnrollmentView.FinalScoreText")
                        : t("Student.EnrollmentView.PartialScoreText")
                    }
                </Typography>
                <Typography variant="body2" fontWeight="bold" component="span" color={score != null ? `${color}.main` : "muted.main"}>
                    &nbsp;{score != null ? `${score}%` : "---"}
                </Typography>

                <MprcProgress
                    value={score || 0}
                    color={color}
                    fullWidth={fullWidth}
                    helperText={
                        <>
                            {(totalItems ?? 0) > 0 ? t(`Student.EnrollmentView.CompletedScoredItemsText${totalItems == 1 ? "_one" : "_other"}`, { x: totalCompletedItems, y: totalItems }) : t("Student.EnrollmentView.AnyScoreText")}
                            <br />
                            {minScore > 0 && t("Student.EnrollmentView.MinFinalScoreText", { x: minScore })}
                        </>
                    }
                />
            </Box>
            {children}
        </>
    );

    return variant === "single" ? RenderSingle : RenderMutilple;
};
